import React, { useState, useEffect, useRef } from "react";
import { db } from "./firebase";
import logo from "./assets/khotba_logo.svg";
import {
  onSnapshot,
  query,
  collection,
  where,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import "./App.css";

function App() {
  const [sessionId, setSessionId] = useState("");
  const [khotba, setKhotba] = useState(null);
  const [error, setError] = useState("");
  const [translations, setTranslations] = useState({});
  const [languages, setLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [showLanguageSelection, setShowLanguageSelection] = useState(false);
  const [listenerDocId, setListenerDocId] = useState(null);

  const inputRef = useRef(null);

  const handleSessionIdSubmit = async () => {
    if (!sessionId.trim()) {
      setError("Please enter a valid Session ID");
      return;
    }

    try {
      const q = query(
        collection(db, "khotbas"),
        where("sessionId", "==", sessionId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError("No Khutbah found with this Session ID");
        setKhotba(null);
        setTranslations({});
        setLanguages([]);
        return;
      }

      const khotbaDoc = querySnapshot.docs[0];
      const khotbaData = khotbaDoc.data();
      const khotbaId = khotbaDoc.id;

      if (khotbaData.khotbaType === "Ended") {
        setError("This Khutbah has ended. You cannot join.");
        return;
      }

      const listenersCollection = collection(
        db,
        "khotbas",
        khotbaId,
        "tvListeners"
      );
      const listenersSnapshot = await getDocs(listenersCollection);

      if (listenersSnapshot.size >= 10) {
        setError("Maximum viewers reached. Try again later.");
        return;
      }

      const listenerDocRef = await addDoc(listenersCollection, {
        createdAt: new Date(),
      });

      setListenerDocId({ id: listenerDocRef.id, khotbaId });

      let langs = [];

      if (khotbaData.isOneTimePaid) {
        langs = khotbaData.selectedLanguages || [];
      } else {
        const userRef = collection(db, "users");
        const userSnapshot = await getDocs(
          query(userRef, where("uid", "==", khotbaData.creatorId))
        );
        if (!userSnapshot.empty) {
          langs = userSnapshot.docs[0].data().selectedLanguages || [];
        }
      }

      setLanguages(langs);
      setKhotba({ id: khotbaId, ...khotbaData });
      setShowLanguageSelection(true);
      setError("");
    } catch (err) {
      console.error("Error fetching Khutbah:", err);
      setError("Failed to fetch Khutbah. Please try again.");
    }
  };

  const handleLanguageSelection = () => {
    if (selectedLanguages.length === 0) {
      setError("Please select at least one language.");
      return;
    }
    setError("");
    setShowLanguageSelection(false);
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!khotba) return;

    const translationsRef = collection(
      db,
      "khotbas",
      khotba.id,
      "translations"
    );

    const unsubscribeTranslations = onSnapshot(
      query(translationsRef),
      (snapshot) => {
        setTranslations((prev) => {
          const updated = { ...prev };
          snapshot.forEach((doc) => {
            const lang = doc.id;
            const data = doc.data();
            const latest = data.translations?.[data.translations.length - 1];
            if (latest) {
              updated[lang] = latest;
            }
          });
          return updated;
        });
      },
      (err) => {
        console.error("Error fetching translations:", err);
        setError("Failed to fetch translations. Please try again.");
      }
    );

    const khotbaDocRef = doc(db, "khotbas", khotba.id);

    const unsubscribeStatus = onSnapshot(
      khotbaDocRef,
      (doc) => {
        const khotbaData = doc.data();
        if (khotbaData?.khotbaType === "Ended") {
          setKhotba(null);
          setTranslations({});
          setLanguages([]);
          setSelectedLanguages([]);
          setShowLanguageSelection(false);
          setSessionId("");
          setError("This Khutbah has ended.");
        }
      },
      (err) => {
        console.error("Error listening to Khutbah status:", err);
      }
    );

    return () => {
      unsubscribeTranslations();
      unsubscribeStatus();
    };
  }, [khotba]);

  const [focusedLangIndex, setFocusedLangIndex] = useState(0);
  const langButtonsRef = useRef([]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showLanguageSelection && languages.length > 0) {
        if (e.key === "ArrowRight") {
          setFocusedLangIndex((prev) => (prev + 1) % languages.length);
        } else if (e.key === "ArrowLeft") {
          setFocusedLangIndex((prev) =>
            prev === 0 ? languages.length - 1 : prev - 1
          );
        } else if (e.key === "Enter") {
          toggleLanguage(languages[focusedLangIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [languages, showLanguageSelection, focusedLangIndex]);

  useEffect(() => {
    const handleUnload = () => {
      if (listenerDocId) {
        const url = `https://us-central1-khutbah-translation.cloudfunctions.net/deleteListener?khotbaId=${listenerDocId.khotbaId}&listenerId=${listenerDocId.id}`;

        navigator.sendBeacon(url);
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [listenerDocId]);
  const toggleLanguage = (lang) => {
    setSelectedLanguages((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]
    );
  };

  return (
    <div className="App">
      <div className="logo-container">
        <img src={logo} alt="Khutbah Logo" className="logo" />
      </div>

      {!khotba ? (
        <div className="session-id-container">
          <h2>Enter Session ID</h2>
          <input
            ref={inputRef}
            type="text"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            placeholder="Session ID"
            className="session-id-input"
          />
          <button onClick={handleSessionIdSubmit} className="submit-button">
            Join Khutbah
          </button>
          {error && <p className="error">{error}</p>}
        </div>
      ) : showLanguageSelection ? (
        <div className="session-id-container">
          <h2>Select Languages to Display</h2>
          <div className="language-selection">
            {languages.map((lang, index) => (
              <button
                key={lang}
                ref={(el) => (langButtonsRef.current[index] = el)}
                tabIndex={0}
                onClick={() => toggleLanguage(lang)}
                className={`language-button ${
                  selectedLanguages.includes(lang) ? "selected" : ""
                }`}
              >
                {lang}
              </button>
            ))}
          </div>
          <button
            onClick={handleLanguageSelection}
            className="submit-button"
            style={{ marginTop: "20px" }}
          >
            Show Translations
          </button>
          {error && <p className="error">{error}</p>}
        </div>
      ) : (
        <div className="translation-container">
          <h2>Live Khutbah Translation: {khotba?.khotbaName}</h2>
          {selectedLanguages.length > 0 ? (
            <div className="translation-list">
              {selectedLanguages.map((language) => {
                const translation = translations[language];
                return (
                  <div key={language} className="translation-card">
                    <div className="language-header">
                      <span className="language-name">{language}</span>
                    </div>
                    <p className="translated-text">
                      {translation?.text || "...."}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p>No languages selected for display.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
